import { useCallback, useEffect, useState } from "react";
import type { AdminDiagnosticsResponse, AdminDiagnosticsTranscript, Locale } from "../../shared/dto";
import { OpenClientLink } from "./OpenClient";
import { TestFilterNote } from "./TestFilterNote";
import { AdminReload, AdminReloadRow } from "./AdminReload";
import { formatDate } from "./core";
import { staffMutation, isOfflineMutation } from "./model";
import "./admin-settings-diagnostics.css";

/**
 * Servis wave 5: the stuck transcript is rescued FROM ITS OWN ROW.
 *
 * The old tool was a select of attachment ids: it asked the admin to know an
 * internal identifier, showed no error text, and after submitting said only
 * that something was sent. The list now starts from the failure: whose file it
 * is, what the provider said, how many attempts it took, and a retry button on
 * that same line. Rows that cannot be retried say so instead of offering a
 * dead button.
 */

export function visibleTranscripts(rows: AdminDiagnosticsTranscript[], hideTestTenants: boolean): AdminDiagnosticsTranscript[] {
  return hideTestTenants ? rows.filter((row) => !row.isTest) : rows;
}

/**
 * 🔴 S3-03: green is the colour of "nothing is stuck", and this panel painted it
 * over a list the filter had emptied - twelve failed transcripts one click away
 * behind "Zobrazit i testovací". Provoz learned this in round two (S2-06) and
 * branches; the rescue tool, whose only question is "visí něco?", still answered
 * an absolute "ne". Same branch, same two sentences: the clear frame is for a
 * list that is empty because nothing is stuck, never for a list that is empty
 * because something is hidden.
 */
export function transcriptEmptyState(hidden: number): { className: string; titleKey: string; hintKey: string } {
  return hidden > 0
    ? {
      className: "staff-empty is-stacked",
      titleKey: "staffConsole.diagnostics.transcripts.emptyFiltered",
      hintKey: "staffConsole.diagnostics.transcripts.emptyFilteredHint"
    }
    : {
      className: "staff-empty is-stacked is-clear",
      titleKey: "staffConsole.diagnostics.transcripts.empty",
      hintKey: "staffConsole.diagnostics.transcripts.emptyHint"
    };
}

// S2-01: a retry that never left the browser is its own state, so the row
// can offer the connection as the thing to fix instead of blaming the
// transcription service a second time.
// S2-16: restarting a transcription is the same size of act as restarting a
// build, and Provoz protects that one with arm-then-confirm while this panel
// fired it on a single click. `armed` is that pause.
type RowState = "idle" | "armed" | "busy" | "done" | "error" | "offline";

export function AdminTranscriptRescue(props: {
  csrfToken: string;
  locale: Locale;
  hideTestTenants: boolean;
  onHideTestTenantsChange?: (hide: boolean) => void;
  onOpenClient?: (clientId: string) => void;
  t: (key: string) => string;
}) {
  const { t } = props;
  const [rows, setRows] = useState<AdminDiagnosticsTranscript[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [states, setStates] = useState<Record<string, RowState>>({});

  const load = useCallback(async () => {
    setFailed(false);
    try {
      const response = await fetch("/api/admin/diagnostics", { credentials: "include" });
      if (!response.ok) throw new Error("diagnostics_failed");
      const body = await response.json() as AdminDiagnosticsResponse;
      setRows(body.transcripts);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  function arm(row: AdminDiagnosticsTranscript) {
    setStates((current) => ({ ...current, [row.id]: "armed" }));
  }

  function cancel(row: AdminDiagnosticsTranscript) {
    setStates((current) => ({ ...current, [row.id]: "idle" }));
  }

  async function retry(row: AdminDiagnosticsTranscript) {
    setStates((current) => ({ ...current, [row.id]: "busy" }));
    const response = await staffMutation(
      `/api/admin/clients/${encodeURIComponent(row.clientId)}/attachments/${encodeURIComponent(row.id)}/retry`,
      { method: "POST", csrfToken: props.csrfToken, body: {} }
    );
    setStates((current) => ({
      ...current,
      [row.id]: response.ok ? "done" : isOfflineMutation(response) ? "offline" : "error"
    }));
  }

  const visible = visibleTranscripts(rows ?? [], props.hideTestTenants);
  const hidden = (rows?.length ?? 0) - visible.length;

  return (
    // S2-12/S2-15: the panel's name and its one sentence live in the summary
    // above (AdminPanel), so the body no longer repeats them under a second
    // heading. The strip that opens the panel carries the count it stands
    // over instead of 1102 px of nothing, and the duplicate copy that used to
    // close the panel is gone.
    <section className="diagnostics-panel" aria-label={t("staffConsole.diagnostics.transcripts.title")}>
      <AdminReloadRow busy={rows === null && !failed} onClick={() => void load()} t={t}>
        {rows !== null && (
          <p>{t("staffConsole.diagnostics.transcripts.count").replace("{count}", String(visible.length))}</p>
        )}
      </AdminReloadRow>
      {/* The note explains the LIST, so it stands where the list starts, the
          same place Provoz and Tým put it. */}
      <TestFilterNote shown={visible.length} hidden={hidden} t={t}
        onShowTest={props.onHideTestTenantsChange && (() => props.onHideTestTenantsChange?.(false))} />
      {failed && (
        <div className="diagnostics-error">
          <p className="message error-message" role="alert">{t("staffConsole.diagnostics.loadError")}</p>
          <AdminReload onClick={() => void load()} t={t} />
        </div>
      )}
      {!failed && rows === null && <p className="staff-empty">{t("common.loading")}</p>}
      {/* S3-03: the frame and the two sentences come from one branch, so a
          filtered list can never be reported as a clean one. */}
      {!failed && rows !== null && visible.length === 0 && (() => {
        const empty = transcriptEmptyState(hidden);
        return (
          <div className={empty.className} role="status">
            <strong>{t(empty.titleKey)}</strong>
            <span>{t(empty.hintKey)}</span>
          </div>
        );
      })()}
      {visible.length > 0 && (
        <ul className="diagnostics-rows">
          {visible.map((row) => (
            <TranscriptRow key={row.id} row={row} state={states[row.id] ?? "idle"} locale={props.locale} t={t}
              onArm={() => arm(row)} onCancel={() => cancel(row)} onRetry={() => void retry(row)}
              onOpenClient={props.onOpenClient} />
          ))}
        </ul>
      )}
    </section>
  );
}

export function TranscriptRow(props: {
  row: AdminDiagnosticsTranscript;
  state: RowState;
  locale: Locale;
  t: (key: string) => string;
  onArm?: () => void;
  onCancel?: () => void;
  onRetry: () => void;
  onOpenClient?: (clientId: string) => void;
}) {
  const { row, t } = props;
  return (
    <li className={`diagnostics-row state-${row.state}`} data-transcript-state={row.state}>
      <div className="diagnostics-row-head">
        <strong>{row.clientName}</strong>
        <span className={row.state === "error" ? "status-chip off" : "status-chip"}>
          {t(`staffConsole.diagnostics.transcripts.state.${row.state}`)}
        </span>
      </div>
      <p className="diagnostics-row-file">
        {[row.fileName ?? t(`staffConsole.diagnostics.scope.${row.scope}`), formatDate(row.updatedAt, props.locale)].join(" · ")}
      </p>
      <p className="diagnostics-row-reason">
        {row.lastError
          ? t("staffConsole.diagnostics.transcripts.reason").replace("{reason}", row.lastError)
          : t("staffConsole.diagnostics.transcripts.reasonUnknown")}
      </p>
      <p className="diagnostics-row-attempts">
        {t("staffConsole.diagnostics.transcripts.attempts").replace("{count}", String(row.attemptCount))}
      </p>
      {/* S2-16: twelve amber primaries under each other meant none of them was
          primary. A row action is secondary; the confirmation that follows is
          the one primary in the row, exactly as Provoz does it. */}
      <div className="diagnostics-row-actions">
        {row.retryable && props.state !== "done" && props.state !== "armed" && (
          <button className="secondary-button compact-button" type="button" disabled={props.state === "busy"}
            onClick={props.onArm ?? props.onRetry}>
            {props.state === "busy"
              ? t("staffConsole.diagnostics.transcripts.retrying")
              : t("staffConsole.diagnostics.transcripts.retry")}
          </button>
        )}
        {!row.retryable && (
          <p className="admin-tool-hint">
            {t(row.state === "stalled"
              ? "staffConsole.diagnostics.transcripts.stalledHint"
              : "staffConsole.diagnostics.transcripts.notRetryable")}
          </p>
        )}
        <OpenClientLink clientId={row.clientId} onOpenClient={props.onOpenClient}
          label={t("staffConsole.activity.openClient")} />
      </div>
      {props.state === "armed" && (
        <div className="diagnostics-row-confirm" role="group"
          aria-label={t("staffConsole.diagnostics.transcripts.confirmTitle")}>
          <p>{t("staffConsole.diagnostics.transcripts.confirmBody")
            .replace("{client}", row.clientName)
            .replace("{file}", row.fileName ?? t(`staffConsole.diagnostics.scope.${row.scope}`))}</p>
          <button className="primary-button compact-button" type="button" onClick={props.onRetry}>
            {t("staffConsole.diagnostics.transcripts.confirm")}
          </button>
          <button className="text-button compact-button" type="button" onClick={props.onCancel}>
            {t("common.cancel")}
          </button>
        </div>
      )}
      {props.state === "done" && (
        <div className="message success-message" role="status">
          <strong>{t("staffConsole.diagnostics.transcripts.doneTitle")}</strong>
          <span>{t("staffConsole.diagnostics.transcripts.doneNext")}</span>
        </div>
      )}
      {props.state === "error" && (
        <div className="message error-message" role="alert">
          <strong>{t("staffConsole.diagnostics.transcripts.failedTitle")}</strong>
          <span>{t("staffConsole.diagnostics.transcripts.failedNext")}</span>
        </div>
      )}
      {props.state === "offline" && (
        <div className="message error-message" role="alert">
          <strong>{t("staffConsole.diagnostics.transcripts.offlineTitle")}</strong>
          <span>{t("staffConsole.mutationOffline")}</span>
        </div>
      )}
    </li>
  );
}
